import { z } from "zod";

export enum ManualJournalRecurringPeriod {
    DAILY = "daily",
    WEEKLY = "weekly",
    MONTHLY = "monthly",
    YEARLY = "yearly",
}

export enum MonthRecurrenceType {
    DAY = "day",
    FIRST_WEEKDAY_OCCURRENCE = "first",
    SECOND_WEEKDAY_OCCURRENCE = "second",
    THIRD_WEEKDAY_OCCURRENCE = "third",
    FOURTH_WEEKDAY_OCCURRENCE = "fourth",
    LAST_WEEKDAY_OCCURRENCE = "last",
}

export const ManualJournalRecurringRuleSchema = z.object({
    endDate: z.string().nullable().optional(),
    period: z.nativeEnum(ManualJournalRecurringPeriod),
    interval: z.number(),
    maxOccurrenceCount: z.number().nullable().optional(),
    weekDay: z.number().nullable().optional(),
    month: z.number().nullable().optional(),
    dateOfMonth: z.number().nullable().optional(),
    monthRecurrenceType: z
        .nativeEnum(MonthRecurrenceType)
        .nullable()
        .optional(),
});

export const RECURRING_RULE_INTERVAL_MUST_BE_POSITIVE =
    "recurringRule.interval must be a positive integer";

export const RECURRING_RULE_MAX_OCCURRENCE_COUNT_MUST_BE_POSITIVE =
    "recurringRule.maxOccurrenceCount must be a positive integer";

interface RecurringRuleCountInput {
    maxOccurrenceCount?: number | null;
}

interface StoredRecurringRuleCount {
    maxOccurrenceCount: number | null;
}

interface MaxOccurrenceCountWriteInput {
    rule: RecurringRuleCountInput;
    storedRule?: StoredRecurringRuleCount | null;
}

export function maxOccurrenceCountWriteMessage({
    rule,
    storedRule,
}: MaxOccurrenceCountWriteInput): string | null {
    const hasInvalidMaxOccurrenceCount =
        rule.maxOccurrenceCount != null &&
        (!Number.isInteger(rule.maxOccurrenceCount) ||
            rule.maxOccurrenceCount < 1);
    const isUnchangedStoredCount =
        storedRule != null &&
        rule.maxOccurrenceCount === storedRule.maxOccurrenceCount;
    if (!hasInvalidMaxOccurrenceCount) {
        return null;
    }
    if (isUnchangedStoredCount) {
        return null;
    }
    return RECURRING_RULE_MAX_OCCURRENCE_COUNT_MUST_BE_POSITIVE;
}

function addPositiveIntervalIssue(
    interval: number,
    ctx: z.RefinementCtx,
): void {
    const hasInvalidInterval = !Number.isInteger(interval) || interval < 1;
    if (hasInvalidInterval) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: RECURRING_RULE_INTERVAL_MUST_BE_POSITIVE,
            path: ["interval"],
        });
    }
}

export const ManualJournalRecurringRuleWriteSchema =
    ManualJournalRecurringRuleSchema.superRefine((data, ctx) => {
        addPositiveIntervalIssue(data.interval, ctx);
        const maxOccurrenceCountMessage = maxOccurrenceCountWriteMessage({
            rule: data,
        });
        if (maxOccurrenceCountMessage) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: maxOccurrenceCountMessage,
                path: ["maxOccurrenceCount"],
            });
        }
    });

export const ManualJournalRecurringRuleUpdateSchema =
    ManualJournalRecurringRuleSchema.superRefine((data, ctx) => {
        addPositiveIntervalIssue(data.interval, ctx);
    });
